import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Chip,
  Paper,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import api from '../config/api';

const ItineraryImportDialog = ({ open, onClose, tripId, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccessMessage('');
      setImportResult(null);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/sample-itinerary-csv`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sample-itinerary-import.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading sample CSV:', error);
      setError('Failed to download sample CSV');
    }
  };

  const handlePreview = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await api.post(`/trips/${tripId}/preview-itinerary-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setPreviewData(response.data);
      setActiveStep(1);
    } catch (error) {
      console.error('Error previewing CSV:', error);
      setError(error.response?.data?.error || 'Failed to preview CSV');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccessMessage('');

    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      const response = await api.post(`/trips/${tripId}/import-itinerary-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data.summary);
      setSuccessMessage(response.data.message);
      setActiveStep(2);
      
      // Close the dialog after showing the success message
      setTimeout(() => {
        if (onImportComplete) {
          onImportComplete(response.data.summary);
        }
        handleClose();
      }, 1000);
    } catch (error) {
      console.error('Error importing CSV:', error);
      setError(error.response?.data?.error || 'Failed to import CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError('');
    setSuccessMessage('');
    setImportResult(null);
    setPreviewData(null);
    setActiveStep(0);
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <UploadIcon color="primary" />
            <Typography variant="h6">Import Itinerary from CSV</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Upload CSV File</StepLabel>
            {activeStep === 0 && (
            <StepContent>
              <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Import multiple itinerary items at once using a CSV file. 
                    <Link 
                      component="button" 
                      onClick={handleDownloadSample}
                      sx={{ ml: 1, textDecoration: 'underline' }}
                    >
                      Download sample CSV
                    </Link>
                    {' '}to see the required format.
                  </Typography>
                </Alert>
              </Box>

              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="csv-file-input"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="csv-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ 
                    py: 2, 
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.light + 10'
                    }
                  }}
                >
                  {file ? file.name : 'Choose CSV File'}
                </Button>
              </label>
              
              {file && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PreviewIcon />}
                    onClick={handlePreview}
                    disabled={uploading}
                  >
                    Preview Import
                  </Button>
                </Box>
              )}
            </StepContent>
            )}
          </Step>

          <Step>
            <StepLabel>Preview & Validate</StepLabel>
            {activeStep === 1 && (
            <StepContent>
              {previewData && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Review the items below. Items with errors will be skipped during import.
                    </Typography>
                  </Alert>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip 
                      label={`Total: ${previewData.summary.total}`} 
                      color="default" 
                      variant="outlined" 
                    />
                    <Chip 
                      label={`Valid: ${previewData.summary.valid}`} 
                      color="success" 
                      variant="outlined" 
                    />
                    {previewData.summary.errors > 0 && (
                      <Chip 
                        label={`Errors: ${previewData.summary.errors}`} 
                        color="error" 
                        variant="outlined" 
                      />
                    )}
                    {previewData.summary.duplicates > 0 && (
                      <Chip 
                        label={`Duplicates: ${previewData.summary.duplicates}`} 
                        color="warning" 
                        variant="outlined" 
                      />
                    )}
                  </Box>

                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Line</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Start Time</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Issues</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.preview.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.line}</TableCell>
                            <TableCell>{row.item.name}</TableCell>
                            <TableCell>{row.item.location}</TableCell>
                            <TableCell>{row.item.category}</TableCell>
                            <TableCell>{row.item.start_datetime}</TableCell>
                            <TableCell>
                              {row.isValid ? (
                                <Chip icon={<CheckIcon />} label="Valid" color="success" size="small" />
                              ) : (
                                <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              {row.errors.length > 0 ? (
                                <Box>
                                  {row.errors.map((error, i) => (
                                    <Typography key={i} variant="caption" color="error" display="block">
                                      {error}
                                    </Typography>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="success">
                                  No issues
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={uploading || previewData.summary.valid === 0}
                    >
                      Import Valid Items ({previewData.summary.valid})
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(0)}
                    >
                      Back to Upload
                    </Button>
                  </Box>
                </Box>
              )}
            </StepContent>
            )}
          </Step>

          <Step>
            <StepLabel>Import Complete</StepLabel>
            {activeStep === 2 && (
            <StepContent>
              {importResult ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Import completed successfully!
                    </Typography>
                  </Alert>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip 
                      label={`Total: ${importResult.total}`} 
                      color="default" 
                      variant="outlined" 
                    />
                    <Chip 
                      label={`Valid: ${importResult.valid}`} 
                      color="info" 
                      variant="outlined" 
                    />
                    <Chip 
                      label={`Imported: ${importResult.imported}`} 
                      color="success" 
                      variant="outlined" 
                    />
                    {importResult.errors > 0 && (
                      <Chip 
                        label={`Errors: ${importResult.errors}`} 
                        color="error" 
                        variant="outlined" 
                      />
                    )}
                    {importResult.duplicates > 0 && (
                      <Chip 
                        label={`Duplicates: ${importResult.duplicates}`} 
                        color="warning" 
                        variant="outlined" 
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {successMessage}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Import in progress...
                  </Typography>
          </Box>
        )}
      </StepContent>
      )}
    </Step>
        </Stepper>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Importing itinerary items...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              {activeStep === 1 ? 'Validating CSV...' : 'Importing itinerary items...'}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {activeStep === 2 ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItineraryImportDialog;
